import { supabase } from '@/lib/supabase';
import type {
  EmploymentType,
  HoursTargetPeriod,
  JobType,
  Staff,
  StaffInvite,
  StaffLocationAssignment,
  InviteAppRole,
  InviteCreateResult,
  InvitePreview,
} from '@/lib/types';

/**
 * Staff + Invites API helpers (Day 4).
 *
 * All mutations go through named DB functions (Solution_Design §1.4):
 *   staff_create / staff_update / staff_set_inactive / staff_reactivate
 *   staff_assign_location / staff_unassign_location
 *   invite_create / invite_resend / invite_revoke / invite_accept
 *   invite_preview                              - public-readable
 *
 * SELECTs go directly against the staff / staff_locations / staff_invites
 * tables; RLS scopes them to the caller's org and role.
 */

// ----- staff: reads ---------------------------------------------------------

export async function listStaff(): Promise<Staff[]> {
  const { data, error } = await supabase
    .from('staff')
    .select('*')
    .order('active_to', { ascending: true, nullsFirst: true })
    .order('display_name', { ascending: true });
  if (error) throw error;
  return (data ?? []) as Staff[];
}

export async function getStaff(id: string): Promise<Staff | null> {
  const { data, error } = await supabase
    .from('staff')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return (data ?? null) as Staff | null;
}

export async function listStaffLocations(
  staffId: string
): Promise<StaffLocationAssignment[]> {
  const { data, error } = await supabase
    .from('staff_locations')
    .select('*')
    .eq('staff_id', staffId);
  if (error) throw error;
  return (data ?? []) as StaffLocationAssignment[];
}

// ----- staff: writes (RPCs) -------------------------------------------------

export interface StaffCreatePayload {
  display_name: string;
  job_type: JobType;
  employment_type: EmploymentType;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  emergency_contact_name?: string | null;
  emergency_contact_phone?: string | null;
  primary_location_id?: string | null;
  active_from?: string;
  location_ids?: string[];
  /** 0081: target floor for scheduled hours. Default 0. */
  min_hours?: number;
  /** 0081: target ceiling for scheduled hours. Default 40. */
  max_hours?: number;
  /** 0081: 'week' or 'month'. Default 'week'. */
  hours_target_period?: HoursTargetPeriod;
  /** 0084: hourly pay rate in CAD. NULL = not set. Pro/Enterprise tier only — server enforces. */
  hourly_rate?: number | null;
  /**
   * 0109: preferred display initials for calendar chip + sidebar avatar.
   * 1-4 chars, uppercase [A-Z.-]. NULL/undefined = auto-derive from name.
   * Server normalizes (trim + upper) and CHECK-validates format.
   */
  custom_initials?: string | null;
}

export async function createStaff(payload: StaffCreatePayload): Promise<Staff> {
  const { data, error } = await supabase.rpc('staff_create', {
    p_display_name: payload.display_name,
    p_job_type: payload.job_type,
    p_employment_type: payload.employment_type,
    p_email: payload.email ?? null,
    p_phone: payload.phone ?? null,
    p_address: payload.address ?? null,
    p_emergency_name: payload.emergency_contact_name ?? null,
    p_emergency_phone: payload.emergency_contact_phone ?? null,
    p_primary_location_id: payload.primary_location_id ?? null,
    p_active_from: payload.active_from ?? new Date().toISOString().slice(0, 10),
    p_location_ids: payload.location_ids ?? [],
    p_min_hours: payload.min_hours ?? 0,
    p_max_hours: payload.max_hours ?? 40,
    p_hours_target_period: payload.hours_target_period ?? 'week',
    p_hourly_rate: payload.hourly_rate ?? null,
    p_custom_initials: payload.custom_initials ?? null,
  });
  if (error) throw error;
  return data as Staff;
}

export type StaffPatch = Partial<{
  display_name: string;
  email: string | null;
  phone: string | null;
  address: string | null;
  emergency_contact_name: string | null;
  emergency_contact_phone: string | null;
  job_type: JobType;
  employment_type: EmploymentType;
  primary_location_id: string | null;
  min_hours: number;
  max_hours: number;
  hours_target_period: HoursTargetPeriod;
  hourly_rate: number | null;
  /** ISO date YYYY-MM-DD. Migration 0096 -- editable by both owner and manager. */
  active_from: string;
  /** ISO date YYYY-MM-DD, or null to clear an end date. Migration 0096. */
  active_to: string | null;
  /**
   * 0109: preferred display initials. NULL clears back to auto-derived.
   * Server normalizes (trim + upper); CHECK enforces format.
   */
  custom_initials: string | null;
}>;

export async function updateStaff(staffId: string, patch: StaffPatch): Promise<Staff> {
  const { data, error } = await supabase.rpc('staff_update', {
    p_staff_id: staffId,
    p_patch: patch,
  });
  if (error) throw error;
  return data as Staff;
}

export async function setStaffInactive(
  staffId: string,
  effectiveDate: string,
  reason: string
): Promise<Staff> {
  const { data, error } = await supabase.rpc('staff_set_inactive', {
    p_staff_id: staffId,
    p_effective_date: effectiveDate,
    p_reason: reason,
  });
  if (error) throw error;
  return data as Staff;
}

export async function reactivateStaff(
  staffId: string,
  effectiveDate: string,
  reason: string
): Promise<Staff> {
  const { data, error } = await supabase.rpc('staff_reactivate', {
    p_staff_id: staffId,
    p_effective_date: effectiveDate,
    p_reason: reason,
  });
  if (error) throw error;
  return data as Staff;
}

export async function assignStaffLocation(
  staffId: string,
  locationId: string,
  roleNote: string | null = null
): Promise<StaffLocationAssignment> {
  const { data, error } = await supabase.rpc('staff_assign_location', {
    p_staff_id: staffId,
    p_location_id: locationId,
    p_role_note: roleNote,
  });
  if (error) throw error;
  return data as StaffLocationAssignment;
}

export async function unassignStaffLocation(
  staffId: string,
  locationId: string,
  reason: string
): Promise<void> {
  const { error } = await supabase.rpc('staff_unassign_location', {
    p_staff_id: staffId,
    p_location_id: locationId,
    p_reason: reason,
  });
  if (error) throw error;
}

// ----- invites --------------------------------------------------------------

export async function listInvites(): Promise<StaffInvite[]> {
  const { data, error } = await supabase
    .from('staff_invites')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as StaffInvite[];
}

export interface InviteCreatePayload {
  staff_id: string;
  app_role: InviteAppRole;
  email: string;
  location_ids?: string[];
  ttl_hours?: number;
}

export async function createInvite(
  payload: InviteCreatePayload
): Promise<InviteCreateResult> {
  const { data, error } = await supabase.rpc('invite_create', {
    p_staff_id: payload.staff_id,
    p_app_role: payload.app_role,
    p_email: payload.email,
    p_location_ids: payload.location_ids ?? [],
    p_ttl_hours: payload.ttl_hours ?? 168,
  });
  if (error) throw error;
  // RPC returns SETOF, supabase-js delivers as array
  const row = Array.isArray(data) ? data[0] : data;
  return row as InviteCreateResult;
}

export async function resendInvite(
  inviteId: string,
  ttlHours = 168
): Promise<InviteCreateResult> {
  const { data, error } = await supabase.rpc('invite_resend', {
    p_invite_id: inviteId,
    p_ttl_hours: ttlHours,
  });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  return row as InviteCreateResult;
}

export async function revokeInvite(
  inviteId: string,
  reason: string
): Promise<StaffInvite> {
  const { data, error } = await supabase.rpc('invite_revoke', {
    p_invite_id: inviteId,
    p_reason: reason,
  });
  if (error) throw error;
  return data as StaffInvite;
}

export async function previewInvite(token: string): Promise<InvitePreview> {
  const { data, error } = await supabase.rpc('invite_preview', { p_token: token });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  return row as InvitePreview;
}

export async function acceptInvite(
  token: string,
  displayName: string | null
): Promise<{ organization_id: string; staff_id: string; role: InviteAppRole }> {
  const { data, error } = await supabase.rpc('invite_accept', {
    p_token: token,
    p_display_name: displayName,
  });
  if (error) throw error;
  return data as { organization_id: string; staff_id: string; role: InviteAppRole };
}

// ----- helpers --------------------------------------------------------------

export function inviteAcceptUrl(token: string): string {
  const origin =
    typeof window !== 'undefined'
      ? window.location.origin
      : 'https://app.pharma-sync.ca';
  return `${origin}/invite/accept/${encodeURIComponent(token)}`;
}

export function isStaffInactive(s: Staff): boolean {
  if (!s.active_to) return false;
  const today = new Date().toISOString().slice(0, 10);
  return s.active_to <= today;
}

// ----- manager promotion / demotion (migration 0091) ------------------------

export async function promoteStaffToManager(
  staffId: string,
  locationId: string
): Promise<Staff> {
  const { data, error } = await supabase.rpc('staff_promote_to_manager', {
    p_staff_id: staffId,
    p_location_id: locationId,
  });
  if (error) throw error;
  return data as Staff;
}

export async function demoteStaffToStaff(staffId: string): Promise<Staff> {
  const { data, error } = await supabase.rpc('staff_demote_to_staff', {
    p_staff_id: staffId,
  });
  if (error) throw error;
  return data as Staff;
}

/** A row from location_managers joined to its location. */
export interface Managership {
  user_id: string;
  location_id: string;
  location_name: string;
}

/**
 * List the current org's manager assignments. RLS scopes this to the caller's
 * organization. Used to render the "Manager" badge on staff list / detail.
 */
export async function listManagerships(): Promise<Managership[]> {
  const { data, error } = await supabase
    .from('location_managers')
    .select('user_id, location_id, locations!inner(name)');
  if (error) throw error;
  type Row = {
    user_id: string;
    location_id: string;
    locations: { name: string } | { name: string }[] | null;
  };
  return (data as Row[]).map((r) => ({
    user_id: r.user_id,
    location_id: r.location_id,
    location_name: Array.isArray(r.locations)
      ? r.locations[0]?.name ?? ''
      : r.locations?.name ?? '',
  }));
}

// ----- invite email auto-send (edge function) -------------------------------

/**
 * Result of a single send attempt. The invite row itself is created/updated
 * separately by invite_create / invite_resend; this is only about the email
 * dispatch step that follows.
 */
export type InviteEmailResult =
  | { sent: true; messageId: string | null }
  | { sent: false; error: string };

/**
 * Ask the send-staff-invite-email Edge Function to email the recipient. The
 * caller has already received (invite_id, raw_token) from invite_create or
 * invite_resend; we forward both. The edge function authorizes the caller as
 * owner of the invite's org and sends a branded email via SES SMTP.
 *
 * Never throws — returns { sent:false, error } on failure so the UI can fall
 * back to "copy the link manually" without losing the just-created invite.
 *
 * NOTE on transport: we use raw fetch() rather than supabase.functions.invoke().
 * The SDK has a quirk where its internal session sync sometimes fires the
 * request without an Authorization header (manifesting as Supabase's
 * UNAUTHORIZED_NO_AUTH_HEADER 503), even when getSession() returns a valid
 * token. Going direct lets us guarantee the header is attached on every call.
 */
export async function sendInviteEmail(
  inviteId: string,
  rawToken: string
): Promise<InviteEmailResult> {
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData.session?.access_token;
    if (!accessToken) {
      return {
        sent: false,
        error: 'Not signed in — please sign in again and retry.',
      };
    }
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
    // Supabase's edge-function gateway currently rejects POSTs whose `apikey`
    // header is the new `sb_publishable_*` format with a 503
    // (UNAUTHORIZED_NO_AUTH_HEADER). Workaround: send the project's legacy
    // anon JWT (HS256, role=anon) here instead. The legacy anon JWT is a
    // public key — safe to ship in the bundle; it grants no privileges
    // beyond what RLS allows for the anon role. Sourced from env so each
    // environment (dev / prod) uses its own project's JWT and the value
    // never gets baked into source. See:
    //   docs/support/2026-05-07_supabase_edge_function_503_apikey.md
    // Once Supabase ticket SU-372060 lands a fix, delete this workaround
    // and the corresponding env var, and let supabase-js attach the
    // `sb_publishable_*` apikey itself.
    const apikey = import.meta.env.VITE_SUPABASE_LEGACY_ANON_KEY as
      | string
      | undefined;
    if (!apikey) {
      return {
        sent: false,
        error:
          'Server is missing VITE_SUPABASE_LEGACY_ANON_KEY (Edge Function gateway workaround). Ask an admin to set it for this environment.',
      };
    }
    const url = `${supabaseUrl.replace(/\/$/, '')}/functions/v1/send-staff-invite-email`;
    let response: Response;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
          apikey,
        },
        body: JSON.stringify({ invite_id: inviteId, raw_token: rawToken }),
      });
    } catch (netErr) {
      return { sent: false, error: 'Network error: ' + (netErr as Error).message };
    }
    let payload: unknown = null;
    try {
      payload = await response.json();
    } catch {
      // non-JSON response (e.g. plain text 503); fall through with payload=null.
    }
    if (!response.ok) {
      const msg =
        payload && typeof payload === 'object' && 'error' in payload
          ? String((payload as { error: unknown }).error)
          : payload && typeof payload === 'object' && 'message' in payload
          ? String((payload as { message: unknown }).message)
          : `HTTP ${response.status}`;
      return { sent: false, error: msg };
    }
    if (payload && typeof payload === 'object' && 'ok' in payload && (payload as { ok: unknown }).ok) {
      const messageId =
        'message_id' in payload && typeof (payload as { message_id?: unknown }).message_id === 'string'
          ? (payload as { message_id: string }).message_id
          : null;
      return { sent: true, messageId };
    }
    return { sent: false, error: 'Unexpected response from email service' };
  } catch (err) {
    return { sent: false, error: (err as Error).message };
  }
}


// ---------------------------------------------------------------------------
// Accept-invite Edge Function (skip second confirmation email)
// ---------------------------------------------------------------------------

/**
 * Result of an accept-staff-invite Edge Function call. On `ok:true` the
 * caller should setSession with the returned tokens and then call
 * acceptInvite() to wire up the invite. On `ok:false` the caller should
 * fall back to the legacy supabase.auth.signUp() flow -- the invitee
 * will get the second confirmation email, but signup still completes.
 */
export type AcceptInviteEdgeResult =
  | {
      ok: true;
      accessToken: string;
      refreshToken: string;
    }
  | {
      ok: false;
      /** Human-readable diagnostic; logged to console.warn, not shown to user. */
      error: string;
    };

/**
 * Ask the accept-staff-invite Edge Function to create an already-confirmed
 * auth user from a valid invite token, bypassing Supabase's default
 * "Confirm your signup" email. This is a public endpoint -- the invitee
 * is not authenticated yet; the invite token is the proof-of-email.
 *
 * Never throws. Returns `{ ok:false, error }` on any failure so the caller
 * can transparently fall back to the legacy supabase.auth.signUp() flow.
 *
 * Uses raw fetch() for the same reason sendInviteEmail() does: the
 * supabase-js Edge Function client occasionally drops the apikey header
 * during session-sync transitions, which the gateway rejects with a 503
 * (UNAUTHORIZED_NO_AUTH_HEADER). Going direct guarantees the headers we
 * need are always attached.
 */
export async function acceptInviteViaEdgeFunction(args: {
  token: string;
  email: string;
  password: string;
  displayName: string;
}): Promise<AcceptInviteEdgeResult> {
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
    // Same legacy-anon-key workaround as sendInviteEmail(); see comment
    // there and docs/support/2026-05-07_supabase_edge_function_503_apikey.md
    // for the gory details. Once Supabase ticket SU-372060 lands, drop
    // this and let supabase-js attach the sb_publishable_* apikey itself.
    const apikey = import.meta.env.VITE_SUPABASE_LEGACY_ANON_KEY as
      | string
      | undefined;
    if (!apikey) {
      return {
        ok: false,
        error:
          'Missing VITE_SUPABASE_LEGACY_ANON_KEY -- Edge Function path unreachable. Falling back to signUp.',
      };
    }
    const url = `${supabaseUrl.replace(/\/$/, '')}/functions/v1/accept-staff-invite`;

    let response: Response;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // No Authorization -- invitee isn't authenticated yet. The
          // function is deployed with verify_jwt=false and uses the
          // invite token itself as the authentication factor.
          apikey,
        },
        body: JSON.stringify({
          token: args.token,
          email: args.email,
          password: args.password,
          displayName: args.displayName,
        }),
      });
    } catch (netErr) {
      return { ok: false, error: 'Network error: ' + (netErr as Error).message };
    }

    let payload: unknown = null;
    try {
      payload = await response.json();
    } catch {
      // Non-JSON 5xx; fall through with payload=null.
    }

    if (!response.ok) {
      const msg =
        payload && typeof payload === 'object' && 'error' in payload
          ? String((payload as { error: unknown }).error)
          : `HTTP ${response.status}`;
      return { ok: false, error: msg };
    }

    if (
      payload &&
      typeof payload === 'object' &&
      'access_token' in payload &&
      'refresh_token' in payload &&
      typeof (payload as { access_token: unknown }).access_token === 'string' &&
      typeof (payload as { refresh_token: unknown }).refresh_token === 'string'
    ) {
      const p = payload as { access_token: string; refresh_token: string };
      return {
        ok: true,
        accessToken: p.access_token,
        refreshToken: p.refresh_token,
      };
    }
    return { ok: false, error: 'Edge Function returned no session tokens' };
  } catch (err) {
    return { ok: false, error: (err as Error).message };
  }
}
